import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Search, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { BlogCard } from "./BlogCard";
import { BlogLayout } from "./BlogLayout";
import { getBlogPosts, getBlogCategories, searchBlogPosts, type BlogPost as BlogPostType, type BlogCategory } from "../services/blog/blogService";
import { BlogPost } from "../../types/blog.types";
import { BlogAdBanner } from "./AdComponent";
import { SEARCH_DEBOUNCE_MS, POSTS_PER_PAGE } from "../constants";

interface BlogListProps {
  onBack: () => void;
  onPostClick: (post: BlogPost) => void;
}

export const BlogList = React.memo(({ onBack, onPostClick }: BlogListProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [allPosts, setAllPosts] = useState<BlogPost[]>([]);
  const [searchedPosts, setSearchedPosts] = useState<BlogPost[] | null>(null);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await getBlogCategories();
        setCategories(cats);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Load posts based on category
  useEffect(() => {
    const loadPosts = async () => {
      setIsLoading(true);
      try {
        const category = selectedCategory === "all" ? undefined : selectedCategory;
        const posts = await getBlogPosts({ category });
        setAllPosts(posts);
      } catch (error) {
        console.error('Error loading posts:', error);
        setAllPosts([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadPosts();
  }, [selectedCategory]);

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when searching
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Get posts based on search
  useEffect(() => {
    const loadSearchResults = async () => {
      if (!debouncedSearchTerm.trim()) {
        setSearchedPosts(null);
        setIsSearching(false);
        return;
      }
      
      setIsSearching(true);
      try {
        const category = selectedCategory === "all" ? undefined : selectedCategory;
        const results = await searchBlogPosts(debouncedSearchTerm, category);
        setSearchedPosts(results);
      } catch (error) {
        console.error('Error searching posts:', error);
        setSearchedPosts([]);
      } finally {
        setIsSearching(false);
      }
    };
    loadSearchResults();
  }, [debouncedSearchTerm, selectedCategory]);

  // Memoize pagination data
  const paginationData = useMemo(() => {
    // Use searched posts if search is active, otherwise use filtered posts
    const postsToPaginate = searchedPosts !== null ? searchedPosts : allPosts;
    const totalPosts = postsToPaginate.length;
    const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);
    const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
    const endIndex = startIndex + POSTS_PER_PAGE;
    const posts = postsToPaginate.slice(startIndex, endIndex);

    return {
      posts,
      totalPosts,
      totalPages,
      hasPrevPage: currentPage > 1,
      hasNextPage: currentPage < totalPages,
    };
  }, [allPosts, searchedPosts, currentPage]);

  const handleCategoryClick = useCallback((categoryId: string) => {
    setSelectedCategory(categoryId);
    setShowFilters(false);
    setCurrentPage(1); // Reset to first page when changing category
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  return (
    <BlogLayout onBack={onBack} title="Blog & Tutorials">
      {/* Top Banner Ad */}
      <BlogAdBanner />
      
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="w-4 h-4" />
            <span>Filter</span>
          </button>
        </div>

        {showFilters && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">Categories</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleCategoryClick("all")}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  selectedCategory === "all"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                All Posts
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedCategory === category.id
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Show loading spinner */}
        {(isLoading || isSearching) ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">
              {isSearching ? 'Searching articles...' : 'Loading articles...'}
            </p>
          </div>
        ) : (
          <>
            <div className="text-sm text-gray-600 mb-4">
              {paginationData.totalPosts} article{paginationData.totalPosts !== 1 ? 's' : ''} found
              {paginationData.totalPages > 1 && (
                <span className="ml-2">
                  (Page {currentPage} of {paginationData.totalPages})
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Only show content when not loading */}
      {!isLoading && !isSearching && (
        <>
          {paginationData.totalPosts === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Search className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No articles found</h3>
              <p className="text-gray-600">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {paginationData.posts.map((post: BlogPost) => (
                  <BlogCard
                    key={post.id}
                    post={post}
                    onClick={() => onPostClick(post)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {paginationData.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!paginationData.hasPrevPage}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(paginationData.totalPages, 5) }, (_, i) => {
                  let page: number;
                  if (paginationData.totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= paginationData.totalPages - 2) {
                    page = paginationData.totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg ${
                        page === currentPage
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                {paginationData.totalPages > 5 && currentPage < paginationData.totalPages - 2 && (
                  <>
                    <span className="px-2 text-gray-500">...</span>
                    <button
                      onClick={() => handlePageChange(paginationData.totalPages)}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      {paginationData.totalPages}
                    </button>
                  </>
                )}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!paginationData.hasNextPage}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
              )}
            </>
          )}
        </>
      )}
    </BlogLayout>
  );
});
